import { contentfulClient } from "./lib/contentful";
import {EntryCollection} from "contentful";
import {AboutUsData} from "./types/aboutUsData";

export interface HeaderData {
    title: string;
    subtitle?: string;
    imageUrl?: string;
}

export interface NavigationData {
    logoUrl?: string;
    items: {
        label: string;
        href: string;
    }[];
}

export interface ProductData {
    id: string;
    title: string;
    description: any;
    imageUrl?: string;
    iconUrl?: string;
    order: number;
}

export type ProcessStepData = {
    name: string;
    description: string;
    order: number;
    icon: {
        fields: {
            title: string;
            file: {
                url: string;
            };
        };
    };
};

type MotionSlide = {
    id: string;
    imageUrl: string;
};

export const getAboutUs = async (): Promise<AboutUsData | null> => {
    const res = await contentfulClient.getEntries({
        content_type: "aboutUsData",
        limit: 1,
        include: 2,
    });

    if (!res.items.length) return null;

    const fields: any = res.items[0].fields;

    return {
        title: fields.title,
        text: fields.text,
        description: fields.description,
        imageUrl: fields.image?.fields?.file?.url
            ? `https:${fields.image.fields.file.url}`
            : undefined,
    };
};

export const getMotionSlides = async (): Promise<MotionSlide[]> => {
    const res = await contentfulClient.getEntries({
        content_type: "motionslide",
        include: 2,
        order: ["fields.order"],
    });

    return res.items.map((item: any) => ({
        id: item.sys.id,
        imageUrl: item.fields.media?.fields?.file?.url
            ? `https:${item.fields.media.fields.file.url}`
            : "",
    }));
};

export const getProductDetails = async (): Promise<ProductData[]> => {
    const res = await contentfulClient.getEntries({
        content_type: "product",
        include: 2,
        order: ["fields.order"],
    });

    return res.items.map((item: any) => {
        const fields = item.fields;

        return {
            id: item.sys.id,
            title: fields.title,
            description: fields.description,
            order: fields.order,
            imageUrl: fields.image?.fields?.file?.url
                ? `https:${fields.image.fields.file.url}`
                : undefined,
            iconUrl: fields.icon?.fields?.file?.url
                ? `https:${fields.icon.fields.file.url}`
                : undefined,
        };
    });
};


export async function getProcessSteps(): Promise<ProcessStepData[]> {
    const res = await contentfulClient.getEntries({
        content_type: "processStep",
        order: ["fields.order"],
    });

    return res.items.map((item: any) => ({
        name: item.fields.name,
        description: item.fields.description,
        order: item.fields.order,
        icon: item.fields.icon,
    }));
}


export const getHeader = async (): Promise<HeaderData | null> => {
    const res = await contentfulClient.getEntries({
        content_type: "header",
        limit: 1,
        include: 2,
    });

    if (!res.items.length) return null;

    const fields: any = res.items[0].fields;

    const imageUrl = fields.image?.fields?.file?.url
        ? `https:${fields.image.fields.file.url}`
        : undefined;

    return {
        title: fields.title,
        subtitle: fields.subtitle,
        imageUrl,
    };
};


export const getNavigation = async (): Promise<NavigationData | null> => {
    const res = await contentfulClient.getEntries({
        content_type: "navigation",
        limit: 1,
        include: 2,
    });

    if (!res.items.length) return null;

    const fields = res.items[0].fields as any;

    const logoUrl = fields.logo?.fields?.file?.url
        ? `https:${fields.logo.fields.file.url}`
        : undefined;

    const items =
        fields.items
            ?.map((item: any) => ({
                label: item.fields.label,
                href: item.fields.href,
                order: item.fields.order,
            }))
            .sort((a: any, b: any) => a.order - b.order) ?? [];

    return {
        logoUrl,
        items,
    };
};

